import { useCallback, useEffect, useRef } from 'react';

import {
  CreateTaskGroupTemplateInput,
  UpdateTaskGroupTemplateInput,
} from '@this+that/models';
import log from 'loglevel';

import {
  useCreateTaskGroupTemplate,
  useDeleteTaskGroupTemplate,
  useGetTaskGroupTemplates,
  useUpdateTaskGroupTemplate,
} from '../../services/taskGroupTemplates.service';
import { useTaskGroupTemplatesStore } from '../../store/TaskGroup/useTaskGroupTemplatesStore';

interface UseTaskGroupTemplatesOptions {
  enabled?: boolean;
}

export function useTaskGroupTemplates(
  taskGroupId: string | null,
  options: UseTaskGroupTemplatesOptions = {},
) {
  const { enabled = true } = options;

  const byGroup = useTaskGroupTemplatesStore((s) => s.byGroup);
  const loadedGroups = useTaskGroupTemplatesStore((s) => s.loadedGroups);
  const loadingByGroup = useTaskGroupTemplatesStore((s) => s.loadingByGroup);
  const { setForGroup, upsert, remove, setGroupLoading } =
    useTaskGroupTemplatesStore();

  const { getTaskGroupTemplates } = useGetTaskGroupTemplates();
  const { createTaskGroupTemplate } = useCreateTaskGroupTemplate();
  const { updateTaskGroupTemplate } = useUpdateTaskGroupTemplate();
  const { deleteTaskGroupTemplate } = useDeleteTaskGroupTemplate();

  const latestTaskGroupIdRef = useRef(taskGroupId);
  latestTaskGroupIdRef.current = taskGroupId;

  useEffect(() => {
    if (!enabled || !taskGroupId || loadedGroups.has(taskGroupId)) {
      return;
    }

    const requestedId = taskGroupId;

    const fetchTemplates = async () => {
      setGroupLoading(requestedId, true);
      try {
        const result = await getTaskGroupTemplates(requestedId);
        if (latestTaskGroupIdRef.current !== requestedId) {
          return;
        }
        setForGroup(requestedId, result);
      } catch (err) {
        log.error('useTaskGroupTemplates fetch failed:', err);
      } finally {
        setGroupLoading(requestedId, false);
      }
    };

    fetchTemplates();
    // MEMORY: project_web_service_hooks_unstable_identity — getTaskGroupTemplates/store
    // setters are fresh each render; depend only on real inputs to avoid a re-fetch loop.
  }, [enabled, taskGroupId, loadedGroups]);

  const templates = taskGroupId ? (byGroup[taskGroupId] ?? []) : [];
  const isLoading = taskGroupId
    ? (loadingByGroup[taskGroupId] ?? false)
    : false;

  const refetch = useCallback(async () => {
    if (!taskGroupId) {
      return;
    }
    setForGroup(taskGroupId, await getTaskGroupTemplates(taskGroupId));
  }, [taskGroupId]);

  const createTemplate = useCallback(
    async (input: CreateTaskGroupTemplateInput) => {
      const created = await createTaskGroupTemplate(input);
      if (created) {
        upsert(created);
      }
      return created;
    },
    [createTaskGroupTemplate, upsert],
  );

  const updateTemplate = useCallback(
    async (input: UpdateTaskGroupTemplateInput) => {
      const updated = await updateTaskGroupTemplate(input);
      if (updated) {
        upsert(updated);
      }
      return updated;
    },
    [updateTaskGroupTemplate, upsert],
  );

  const deleteTemplate = useCallback(
    async (id: string) => {
      if (!taskGroupId) {
        return;
      }
      const prevList =
        useTaskGroupTemplatesStore.getState().byGroup[taskGroupId] ?? [];
      remove(taskGroupId, id);
      try {
        await deleteTaskGroupTemplate(taskGroupId, id);
      } catch (err) {
        setForGroup(taskGroupId, prevList);
        log.error('useTaskGroupTemplates delete failed:', err);
        throw err;
      }
    },
    // MEMORY: project_web_service_hooks_unstable_identity — remove/setForGroup/delete getters are
    // stable-by-construction or read via getState(); depend only on taskGroupId.
    [taskGroupId],
  );

  return {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    refetch,
  };
}
